import type React from "react";
import { useEffect, useRef, useState } from "react";
import { getAttachmentViewingUrl } from "@/shared/services/uploadService";
import { formatFileSize, getMimeType } from "@/shared/utils/fileHelpers";
import type { AnnouncementAttachment } from "@/features/announcements/types/feed";
import * as pdfjsLib from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.mjs",
  import.meta.url,
).toString();

interface AttachmentPreviewProps {
  attachments: AnnouncementAttachment[];
}

/**
 * Extract a filename from an S3 presigned URL.
 * e.g. "https://…/announcements/2026-02-01/1769985862992-file.pdf?X-Amz-…" → "file.pdf"
 */
const filenameFromUrl = (url: string): string => {
  try {
    const path = new URL(url).pathname; // "/announcements/…/1769985862992-file.pdf"
    return path.split("/").pop() ?? "";
  } catch {
    return "";
  }
};

/**
 * Resolve MIME type using all available sources:
 * 1. file_type column (if backend provides it)
 * 2. display_name extension
 * 3. s3_key extension
 * 4. resolved viewUrl (S3 presigned URL path)
 */
const resolveType = (
  att: AnnouncementAttachment,
  viewUrl?: string | null,
): string => {
  if (att.file_type) return att.file_type;

  const fromName = getMimeType(att.display_name ?? "");
  if (fromName !== "application/octet-stream") return fromName;

  const fromKey = getMimeType(att.s3_key ?? "");
  if (fromKey !== "application/octet-stream") return fromKey;

  if (viewUrl) {
    const fromUrl = getMimeType(filenameFromUrl(viewUrl));
    if (fromUrl !== "application/octet-stream") return fromUrl;
  }

  return "application/octet-stream";
};

/* ── PDF thumbnail rendered to a <canvas> ── */
const PdfThumbnail = ({
  url,
  onReady,
}: {
  url: string;
  onReady: () => void;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const pdf = await pdfjsLib.getDocument(url).promise;
        const page = await pdf.getPage(1);

        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;

        const targetW = 480; // 2× for retina
        const vp = page.getViewport({ scale: 1 });
        const scale = targetW / vp.width;
        const scaled = page.getViewport({ scale });

        canvas.width = scaled.width;
        canvas.height = scaled.height;

        await page.render({
          canvasContext: canvas.getContext("2d")!,
          viewport: scaled,
          canvas,
        }).promise;

        if (!cancelled) onReady();
      } catch {
        /* fail silently – the fallback icon stays visible */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url, onReady]);

  return (
    <canvas
      ref={canvasRef}
      className="w-[240px] h-[180px] object-cover rounded-lg border border-gray-200 group-hover:border-indigo-300 transition-colors"
    />
  );
};

/* ── Per-attachment card ── */
const PdfPreviewPage = ({
  pdf,
  pageNumber,
  scale,
}: {
  pdf: PDFDocumentProxy;
  pageNumber: number;
  scale: number;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const page = await pdf.getPage(pageNumber);
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      if (!canvas || cancelled) return;

      const context = canvas.getContext("2d");
      if (!context) return;

      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: context, viewport, canvas }).promise;
    })();

    return () => {
      cancelled = true;
    };
  }, [pdf, pageNumber, scale]);

  return (
    <canvas
      ref={canvasRef}
      className="block max-w-full h-auto bg-white shadow-sm border border-gray-200"
    />
  );
};

const PdfPreviewModal = ({
  url,
  name,
  onClose,
}: {
  url: string;
  name: string;
  onClose: () => void;
}) => {
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [error, setError] = useState(false);
  const [scale, setScale] = useState(1.35);

  useEffect(() => {
    let cancelled = false;
    setPdf(null);
    setError(false);

    pdfjsLib
      .getDocument(url)
      .promise.then((loadedPdf) => {
        if (!cancelled) setPdf(loadedPdf);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const pages = pdf
    ? Array.from({ length: pdf.numPages }, (_, index) => index + 1)
    : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-6">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-5xl h-[88vh] bg-white rounded-lg shadow-xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
            <p className="text-xs text-gray-500">
              {pdf ? `${pdf.numPages} page${pdf.numPages === 1 ? "" : "s"}` : "Loading preview..."}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => setScale((value) => Math.max(0.8, value - 0.15))}
              className="px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
              title="Zoom out"
            >
              -
            </button>
            <button
              type="button"
              onClick={() => setScale((value) => Math.min(2, value + 0.15))}
              className="px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
              title="Zoom in"
            >
              +
            </button>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="px-2 py-1 text-sm text-indigo-600 hover:bg-indigo-50 rounded"
            >
              Open
            </a>
            <button
              type="button"
              onClick={onClose}
              className="px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
              title="Close"
            >
              Close
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-gray-100 p-3 md:p-5">
          {!pdf && !error && (
            <div className="h-full flex items-center justify-center text-sm text-gray-500">
              Loading PDF preview...
            </div>
          )}
          {error && (
            <div className="h-full flex flex-col items-center justify-center gap-3 text-sm text-gray-600">
              <p>Preview is unavailable for this PDF.</p>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="text-indigo-600 hover:text-indigo-800"
              >
                Open in a new tab
              </a>
            </div>
          )}
          {pdf && (
            <div className="mx-auto flex w-fit max-w-full flex-col items-center gap-5">
              {pages.map((pageNumber) => (
                <PdfPreviewPage
                  key={`${pageNumber}-${scale}`}
                  pdf={pdf}
                  pageNumber={pageNumber}
                  scale={scale}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AttachmentPreviewCard = ({
  attachment,
}: {
  attachment: AnnouncementAttachment;
}) => {
  const [viewUrl, setViewUrl] = useState<string | null>(null);
  const [pdfReady, setPdfReady] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  // Shared by the <button> card and the <div> video card (observer target)
  const cardRef = useRef<HTMLElement | null>(null);

  // Only fetch the presigned URL (and trigger image download) when the card
  // enters the viewport. Cards below the fold never hit S3.
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsInView(true); },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isInView) return;
    getAttachmentViewingUrl(attachment.attachment_id)
      .then(setViewUrl)
      .catch(() => {});
  }, [isInView, attachment.attachment_id]);

  // Re-evaluate type every render (once viewUrl resolves, type may change)
  const resolved = resolveType(attachment, viewUrl);
  const image = resolved.startsWith("image/");
  const pdf = resolved === "application/pdf";
  const video = resolved.startsWith("video/");

  const name =
    attachment.display_name ||
    (attachment.s3_key ? attachment.s3_key.split("/").pop() : "") ||
    (viewUrl ? filenameFromUrl(viewUrl) : "");
  const ext = name?.split(".").pop()?.toUpperCase() || "FILE";

  const handleClick = () => {
    if (!viewUrl) return;
    if (pdf) {
      setPreviewOpen(true);
      return;
    }
    window.open(viewUrl, "_blank");
  };

  // ── Video: inline click-to-play player (not wrapped in the button card,
  // so the native controls aren't hijacked by the open-in-new-tab handler) ──
  if (video) {
    return (
      <div ref={cardRef as React.RefObject<HTMLDivElement>} className="shrink-0">
        {viewUrl ? (
          <video
            src={viewUrl}
            controls
            preload="metadata"
            playsInline
            controlsList="nodownload"
            className="w-[240px] h-[180px] object-contain rounded-lg border border-gray-200 bg-black"
          />
        ) : (
          <div className="w-[240px] h-[180px] flex items-center justify-center bg-gray-900 rounded-lg border border-gray-200 animate-pulse">
            <svg
              className="w-10 h-10 text-gray-500"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        )}
        <p className="text-xs text-gray-600 mt-1 truncate w-[240px]">{name}</p>
      </div>
    );
  }

  return (
    <div className="shrink-0">
      <button
        ref={cardRef as React.RefObject<HTMLButtonElement>}
        type="button"
        onClick={handleClick}
        className="text-left group cursor-pointer"
      >
      {/* ── Image preview ── */}
      {image && viewUrl && (
        <img
          src={viewUrl}
          alt={name}
          loading="lazy"
          className="w-[240px] h-[180px] object-cover rounded-lg border border-gray-200 group-hover:border-indigo-300 transition-colors"
        />
      )}

      {/* ── Image loading placeholder ── */}
      {image && !viewUrl && (
        <div className="w-[240px] h-[180px] flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200 animate-pulse">
          <svg
            className="w-8 h-8 text-gray-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z"
            />
          </svg>
        </div>
      )}

      {/* ── PDF preview (rendered first page) ── */}
      {pdf && (
        <div className="relative w-[240px] h-[180px]">
          {viewUrl && (
            <PdfThumbnail url={viewUrl} onReady={() => setPdfReady(true)} />
          )}
          {!pdfReady && (
            <div
              className={`${viewUrl ? "absolute inset-0" : ""} w-[240px] h-[180px] flex flex-col items-center justify-center bg-red-50 rounded-lg border border-gray-200 group-hover:border-indigo-300 transition-colors`}
            >
              <svg
                className="w-8 h-8 text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
              <span className="text-[10px] font-medium text-red-500 mt-1">
                PDF
              </span>
              {attachment.file_size != null && (
                <span className="text-[10px] text-gray-400">
                  {formatFileSize(attachment.file_size)}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Loading placeholder (type unknown until URL resolves) ── */}
      {!image && !pdf && !viewUrl && (
        <div className="w-[240px] h-[180px] flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200 animate-pulse">
          <svg
            className="w-8 h-8 text-gray-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
        </div>
      )}

      {/* ── Other file types (type known but not image/pdf) ── */}
      {!image && !pdf && viewUrl && (
        <div className="w-[240px] h-[180px] flex flex-col items-center justify-center bg-gray-50 rounded-lg border border-gray-200 group-hover:border-indigo-300 transition-colors">
          <svg
            className="w-8 h-8 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
          <span className="text-[10px] font-medium text-gray-500 mt-1">
            {ext}
          </span>
          {attachment.file_size != null && (
            <span className="text-[10px] text-gray-400">
              {formatFileSize(attachment.file_size)}
            </span>
          )}
        </div>
      )}

        <p className="text-xs text-gray-600 mt-1 truncate w-[240px]">{name}</p>
      </button>
      {pdf && previewOpen && viewUrl && (
        <PdfPreviewModal
          url={viewUrl}
          name={name}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
};

export const AttachmentPreview = ({ attachments }: AttachmentPreviewProps) => {
  if (!attachments || attachments.length === 0) return null;

  return (
    <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
      {attachments.map((att) => (
        <AttachmentPreviewCard key={att.attachment_id} attachment={att} />
      ))}
    </div>
  );
};
